import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/services";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const navLinkClass = ({ isActive }) =>
    `font-outfit text-[15px] font-medium tracking-wide transition-colors ${
      isActive ? "text-terracotta" : "text-forest hover:text-terracotta"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-cream/90 backdrop-blur-xl border-b border-soft" : "bg-cream"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="header-logo" className="flex items-center group" aria-label="Perspective Assurance — Accueil">
            <img
              src="/logo.png"
              alt="Perspective Assurance"
              className="h-10 sm:h-11 lg:h-12 w-auto object-contain"
              loading="eager"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Navigation principale">
            <NavLink to="/" end className={navLinkClass} data-testid="nav-home">Accueil</NavLink>
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <button data-testid="nav-services" className="font-outfit text-[15px] font-medium text-forest hover:text-terracotta transition-colors">
                Nos services
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-80">
                  <div className="bg-white border border-soft rounded-2xl shadow-xl p-3">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/${s.slug}`}
                        data-testid={`nav-service-${s.slug}`}
                        className="block px-4 py-3 rounded-xl hover:bg-cream transition-colors"
                      >
                        <div className="font-outfit font-medium text-forest">{s.title}</div>
                        <div className="text-sm text-forest/60 mt-0.5">{s.short}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/a-propos" className={navLinkClass} data-testid="nav-about">À propos</NavLink>
            <NavLink to="/blog" className={navLinkClass} data-testid="nav-blog">Blog</NavLink>
            <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact">Contact</NavLink>
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${COMPANY.phone}`}
              data-testid="header-call-btn"
              className="flex items-center gap-2 text-forest hover:text-terracotta transition-colors font-outfit font-medium text-sm"
            >
              <Phone className="w-4 h-4" />
              {COMPANY.phoneDisplay}
            </a>
            <Link
              to="/contact"
              data-testid="header-quote-btn"
              className="bg-terracotta hover:bg-[#1E3F73] text-white rounded-full px-6 py-3 font-outfit font-medium text-sm shadow-md hover:shadow-lg transition-all"
            >
              Devis gratuit
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-forest"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="lg:hidden bg-cream border-t border-soft" data-testid="mobile-menu">
          <nav className="px-6 py-6 space-y-2">
            <NavLink to="/" end className="block py-3 font-outfit font-medium text-forest" data-testid="mobile-nav-home">Accueil</NavLink>
            <div className="py-3">
              <div className="font-outfit font-medium text-forest/60 text-xs uppercase tracking-[0.2em] mb-2">Services</div>
              {SERVICES.map((s) => (
                <NavLink
                  key={s.slug}
                  to={`/${s.slug}`}
                  data-testid={`mobile-nav-service-${s.slug}`}
                  className="block py-2 pl-3 font-outfit text-forest border-l-2 border-soft hover:border-terracotta"
                >
                  {s.title}
                </NavLink>
              ))}
            </div>
            <NavLink to="/a-propos" className="block py-3 font-outfit font-medium text-forest" data-testid="mobile-nav-about">À propos</NavLink>
            <NavLink to="/blog" className="block py-3 font-outfit font-medium text-forest" data-testid="mobile-nav-blog">Blog</NavLink>
            <NavLink to="/contact" className="block py-3 font-outfit font-medium text-forest" data-testid="mobile-nav-contact">Contact</NavLink>
            <div className="pt-4 flex flex-col gap-3">
              <a href={`tel:${COMPANY.phone}`} className="flex items-center justify-center gap-2 border border-forest text-forest rounded-full py-3 font-outfit font-medium" data-testid="mobile-call-btn">
                <Phone className="w-4 h-4" /> Appeler
              </a>
              <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-terracotta text-white rounded-full py-3 font-outfit font-medium" data-testid="mobile-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
